import React from 'react'
import styled from 'styled-components'

import { Helmet } from 'react-helmet'

import { Box, Divider, Paper, Typography } from '@material-ui/core'

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: ${(props) => props.theme.spacing(8)}px ${(props) => props.theme.spacing(4)}px;
  background: ${(props) => props.theme.palette.background.default};
`

const Content = styled(Paper)`
  max-width: 860px;
  width: 100%;
  padding: ${(props) => props.theme.spacing(8)}px;

  ${(props) => props.theme.breakpoints.down('sm')} {
    padding: ${(props) => props.theme.spacing(4)}px;
  }
`

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box mt={4}>
    <Typography variant="h6" gutterBottom>{title}</Typography>
    <Typography variant="body2" color="textSecondary" component="div">
      {children}
    </Typography>
  </Box>
)

export const PrivacyPolicy = () => {
  return (
    <Wrapper>
      <Helmet title="Privacy Policy" />
      <Content>
        <Typography variant="h4" gutterBottom>Privacy Policy</Typography>
        <Typography variant="body2" color="textSecondary">
          Last updated: February 19, 2026
        </Typography>

        <Box mt={2}>
          <Typography variant="body1">
            SKUit ("we", "our", or "us") operates a warehouse management platform. This Privacy Policy
            describes how we collect, use, and safeguard information when you use our services.
          </Typography>
        </Box>

        <Divider style={{ marginTop: 24 }} />

        <Section title="1. Information We Collect">
          <p>We collect information you provide directly, including:</p>
          <ul>
            <li>Account credentials (username, email address, phone)</li>
            <li>Business and contact information</li>
            <li>Operational data entered into the platform (inventory, orders, shipments, facilities)</li>
            <li>Activity logs and audit trails generated through use of the system</li>
          </ul>
          <p>We also collect technical data automatically, such as IP addresses, browser type, and access timestamps, for security and system reliability purposes.</p>
        </Section>

        <Section title="2. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, operate, and maintain the platform</li>
            <li>Authenticate users and enforce access controls</li>
            <li>Process and fulfill operational requests (orders, shipments, inventory actions)</li>
            <li>Generate reports and audit logs for your organization</li>
            <li>Investigate security incidents and prevent unauthorized access</li>
            <li>Communicate service updates, maintenance windows, or critical alerts</li>
          </ul>
        </Section>

        <Section title="3. Data Sharing">
          <p>We do not sell your personal information. We may share data with:</p>
          <ul>
            <li><strong>Service providers</strong> who assist in operating our infrastructure (hosting, monitoring) under strict confidentiality obligations</li>
            <li><strong>Your organization</strong> — data entered on behalf of a client account is accessible to authorized members of that account</li>
            <li><strong>Law enforcement</strong> when required by applicable law or valid legal process</li>
          </ul>
        </Section>

        <Section title="4. Data Retention">
          <p>
            We retain operational data for as long as your account is active or as required to fulfill
            contractual obligations. Audit logs and activity records may be retained for longer periods
            for compliance purposes. You may request deletion of your account and associated data by
            contacting us.
          </p>
        </Section>

        <Section title="5. Security">
          <p>
            We implement industry-standard security measures including encryption in transit (TLS),
            access controls, and activity monitoring. However, no system is completely secure, and we
            cannot guarantee absolute security of transmitted data.
          </p>
        </Section>

        <Section title="6. Your Rights">
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data, subject to legal retention requirements</li>
            <li>Object to or restrict certain processing activities</li>
          </ul>
          <p>To exercise these rights, contact us at the address below.</p>
        </Section>

        <Section title="7. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material changes
            by posting the updated policy with a revised date. Continued use of the platform after
            changes constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section title="8. Contact Us">
          <p>
            If you have questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <p>
            <strong>SKUit</strong><br />
            admin@skuit.com
          </p>
        </Section>
      </Content>
    </Wrapper>
  )
}
